import type { QuestionAnswer, QuestionRequest } from "@mux/sdk/v2/client"
import type { RequestReply } from "@/transport/request-replies"
import type { OpenRequests } from "@/transport/requests"
import type { RequestSnapshotController } from "./request-snapshot"

type Request = Pick<QuestionRequest, "id" | "sessionID">
const responseTimeoutMs = 30_000

export function createQuestionResponseController(input: {
  snapshots: RequestSnapshotController
  send: (directory: string, requestID: string, answers: QuestionAnswer[] | undefined, signal: AbortSignal) => Promise<RequestReply>
  load: (signal: AbortSignal) => Promise<OpenRequests>
}) {
  const lifetime = new AbortController()
  const signal = () => AbortSignal.any([lifetime.signal, AbortSignal.timeout(responseTimeoutMs)])
  return {
    /** Resolves "closed" when the question no longer waited for this reply; either way it leaves the store. */
    async respond(directory: string, request: Request, answers?: QuestionAnswer[]) {
      try {
        const reply = await input.send(directory, request.id, answers, signal())
        if (!lifetime.signal.aborted) input.snapshots.resolveQuestion(request)
        return reply
      } catch (error) {
        if (lifetime.signal.aborted) throw error
        // A failed response can still have reached the server. Reconcile, never replay the write.
        await input.snapshots.refresh(() => input.load(signal())).catch((refreshError) => {
          if (!lifetime.signal.aborted) console.warn("Failed to reconcile pending questions", refreshError)
        })
        throw error
      }
    },
    dispose: () => lifetime.abort(),
  }
}
